import type { TransactionRow } from '@mentora/data-access'
import type { PriceFormatter } from '../price-formatter'
import { Badge, Card, CardContent, CardHeader, CardTitle } from '@mentora/ui-components'
import { useFormatter, useTranslations } from 'next-intl'
import { TransactionDetailsDialog } from './transaction-details-dialog'
import { useStatusLabel } from './use-status-label'

type BadgeVariant
  = 'default' | 'secondary' | 'outline' | 'success' | 'warning' | 'destructive'

// Course/event (pending|completed|refunded|failed|abandoned|cancelled) and
// membership (incomplete|active|past_due|canceled) statuses, mapped to one
// badge scale.
const STATUS_VARIANT: Record<string, BadgeVariant> = {
  completed: 'success',
  active: 'success',
  pending: 'secondary',
  incomplete: 'secondary',
  refunded: 'warning',
  past_due: 'warning',
  failed: 'destructive',
  abandoned: 'outline',
  canceled: 'destructive',
  cancelled: 'outline',
}

/**
 * Admin transactions: course and event purchases and memberships.
 *
 * Copy comes from the storefront's catalogue under `transactions`, like every
 * other string in this library; money comes from `formatPrice`, because the
 * currency is a storefront fact rather than a locale one. This component used
 * to hardcode its English and format dates with the *server's* locale, so a
 * Portuguese admin on Formed read "Transactions / Past due" and a US-style date.
 *
 * Labels are literal `t()` calls (`useStatusLabel`), not `t(`status.${x}`)`: the
 * catalogue-coverage spec reads keys statically, and a key it cannot see is a
 * key it cannot check.
 */
export function TransactionsTable({
  rows,
  formatPrice,
  listCurrency,
}: {
  rows: TransactionRow[]
  formatPrice: PriceFormatter
  /** The storefront's list currency, for the details dialog (a formatter cannot cross into it). */
  listCurrency: string
}) {
  const t = useTranslations('transactions')
  const format = useFormatter()

  const statusLabel = useStatusLabel()
  const kindLabel: Record<string, string> = {
    course: t('kind.course'),
    event: t('kind.event'),
    membership: t('kind.membership'),
  }

  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="p-0">
          <p className="px-6 py-12 text-center text-sm text-muted-foreground">
            {t('empty')}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead className="border-b border-border/60 bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">{t('columns.customer')}</th>
                <th className="px-4 py-3 font-medium">{t('columns.item')}</th>
                <th className="px-4 py-3 font-medium">{t('columns.type')}</th>
                <th className="px-4 py-3 font-medium">{t('columns.status')}</th>
                <th className="px-4 py-3 text-right font-medium">{t('columns.amount')}</th>
                <th className="px-4 py-3 font-medium">{t('columns.provider')}</th>
                <th className="px-4 py-3 text-right font-medium">{t('columns.date')}</th>
                {/* Pinned to the right edge, so the button stays in reach while a phone scrolls the table. */}
                <th className="sticky right-0 bg-muted px-2 py-3"><span className="sr-only">{t('columns.actions')}</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {rows.map(row => (
                <tr key={`${row.kind}-${row.id}`} className="align-top">
                  <td className="px-4 py-3">
                    <p className="text-foreground">{row.customerName ?? '—'}</p>
                    {row.customerEmail && (
                      <p className="text-xs text-muted-foreground">{row.customerEmail}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-foreground">{row.itemLabel}</p>
                    {row.itemSlug && (
                      <p className="text-xs text-muted-foreground">
                        /
                        {row.itemSlug}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline">
                      {kindLabel[row.kind] ?? row.kind}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={STATUS_VARIANT[row.status] ?? 'secondary'}>
                      {statusLabel(row.status)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-foreground">
                    {row.amountCents === null ? '—' : formatPrice(row.amountCents)}
                    {row.refundedCents !== null && row.refundedCents > 0 && (
                      <p className="text-xs text-muted-foreground">{t('refundedAmount', { amount: formatPrice(row.refundedCents) })}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <p className="capitalize text-foreground">{row.provider}</p>
                    {row.reference && (
                      <p className="max-w-[180px] truncate text-xs text-muted-foreground" title={row.reference}>
                        {row.reference}
                      </p>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-muted-foreground">
                    <p>{format.dateTime(new Date(row.createdAt), { year: 'numeric', month: 'short', day: 'numeric' })}</p>
                    <p className="text-xs tabular-nums">
                      {format.dateTime(new Date(row.createdAt), { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </td>
                  <td className="sticky right-0 bg-card px-2 py-3 text-right">
                    <TransactionDetailsDialog
                      kind={row.kind}
                      id={row.id}
                      label={`${row.customerName ?? row.customerEmail ?? '—'} · ${row.itemLabel}`}
                      listCurrency={listCurrency}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}
